import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './rush.css';
import './pricing.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';

// Per-plan marketing copy. Numbers (price, limits, seat counts) come from
// /api/plans at render time so this file can't drift from accountPlans.js;
// only the strings here need hand-tuning.
const PLAN_COPY = {
  free:      { name: 'Free',      description: 'For solo translators and first projects.',  cta: 'Start free',    highlight: false },
  essential: { name: 'Essential', description: 'Perfect for small teams and startups.',     cta: 'Get Started',   highlight: false },
  growth:    { name: 'Growth',    description: 'For growing companies with global reach.',  cta: 'Upgrade Now',   highlight: true },
  scale:     { name: 'Scale',     description: 'Advanced features for large scale operations.', cta: 'Contact Sales', highlight: false },
};
const PLAN_ORDER = ['free', 'essential', 'growth', 'scale'];

// Formatters shared with src/components/Pricing.js — both pages quote the
// same plan in similar phrasing, so worth keeping the strings aligned.
const fmtProjects = (n) => (n == null ? 'Unlimited Projects' : `${n} Project${n === 1 ? '' : 's'}`);
// Overall workspace translated-words allowance (human + TM + AI).
const fmtWords = (n) => {
  if (n == null) return 'Unlimited translated words / mo';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M translated words / mo`;
  return `${(n / 1_000).toFixed(0)}k translated words / mo`;
};
// AI / machine-translation sub-cap (the credit-costing subset).
const fmtAIWords = (n) => {
  if (n == null) return 'Unlimited AI words / mo';
  if (!n) return 'No AI translation';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M AI words / mo`;
  return `${(n / 1_000).toFixed(0)}k AI words / mo`;
};
const fmtSeats = (mgr, ling) => {
  if (mgr == null && ling == null) return 'Unlimited manager + linguist seats';
  if (mgr === 0 && ling === 0) return 'Solo workspace · no teammates';
  return `${mgr ?? '∞'} manager + ${ling ?? '∞'} linguist seats`;
};
const fmtApps = (apps) => {
  if (apps == null) return 'Unlimited App Integrations';
  if (apps === 0) return 'No App Integrations';
  return apps === 1 ? '1 App Integration' : `${apps} App Integrations`;
};
const fmtSupport = (s) =>
  ({ email: 'Email Support', premium: 'Priority Support', dedicated: 'Dedicated Support', community: 'Community Support' }[s] || 'Email Support');

const buildPlanCard = (apiPlan) => {
  const copy = PLAN_COPY[apiPlan.key] || { name: apiPlan.label, description: '', cta: 'Get started', highlight: false };
  return {
    name: copy.name,
    slug: apiPlan.key,
    monthly: apiPlan.contactSales ? null : apiPlan.monthlyGbp,
    yearly: apiPlan.contactSales ? null : apiPlan.yearlyGbp,
    yearlyMonthsBilled: apiPlan.yearlyMonthsBilled,
    description: copy.description,
    features: [
      fmtProjects(apiPlan.limits.projects),
      fmtWords(apiPlan.limits.translatedWords),
      fmtAIWords(apiPlan.limits.aiWords),
      fmtSeats(apiPlan.limits.managerSeats, apiPlan.limits.linguistSeats),
      fmtApps(apiPlan.limits.apps),
      fmtSupport(apiPlan.support),
    ],
    cta: copy.cta,
    highlight: copy.highlight,
  };
};

// Build the "Usage" rows of the compare table from the API plans. Other
// rows (booleans, marketing copy) stay static below.
const cell = (v, suffix = '') => (v == null ? 'Unlimited' : `${v.toLocaleString()}${suffix}`);
const buildUsageRows = (orderedApi) => [
  ['Projects', ...orderedApi.map((p) => cell(p.limits.projects))],
  ['Translated words / month', ...orderedApi.map((p) => p.limits.translatedWords == null ? 'Unlimited' : p.limits.translatedWords.toLocaleString())],
  ['AI words / month', ...orderedApi.map((p) => p.limits.aiWords == null ? 'Unlimited' : p.limits.aiWords.toLocaleString())],
  ['Languages', '100+', '100+', '100+', '100+'],
  ['Manager seats', ...orderedApi.map((p) => cell(p.limits.managerSeats))],
  ['Linguist seats', ...orderedApi.map((p) => cell(p.limits.linguistSeats))],
];

// Where each plan's CTA leads. Scale is sales-led; the rest pre-select the plan
// on the registration page (RushSignUpPage reads ?plan=…).
const planTarget = (plan) => (plan.slug === 'scale' ? '/contact' : `/register?plan=${plan.slug}`);

const STATIC_COMPARE_GROUPS = [
  {
    label: 'Translation features',
    rows: [
      ['AI machine translation', true, true, true, true],
      ['Translation memory (TM)', true, true, true, true],
      ['Glossaries', false, '1 per workspace', 'Per client', 'Per client + on-prem'],
      ['Side-by-side editor', true, true, true, true],
      ['Real-time review & comments', false, false, true, true],
      ['Custom MT engines', false, false, false, true],
    ],
  },
  {
    label: 'Integrations',
    rows: [
      ['App integrations', false, '1', '5', 'Unlimited'],
      ['Google Drive · Dropbox · OneDrive', false, true, true, true],
      ['Slack · Notion · GitHub', false, false, true, true],
      ['API + webhooks', false, false, true, true],
    ],
  },
  {
    label: 'Security & support',
    rows: [
      ['SOC 2 + GDPR', true, true, true, true],
      ['SSO & SAML', false, false, 'Add-on', true],
      ['SCIM provisioning', false, false, false, true],
      ['Audit logs', false, false, false, true],
      ['Support', 'Community', 'Email', 'Priority email + chat', 'Dedicated CSM + SLA'],
    ],
  },
];

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M3.5 9.5l3.5 3.5 7-7" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Dash = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3 7h8" stroke="var(--ink-4)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CompareCell = ({ v }) => {
  if (v === true) return <span className="cmp-true"><Check /></span>;
  if (v === false) return <span className="cmp-false"><Dash /></span>;
  return <span className="cmp-text">{v}</span>;
};

const PricingHero = ({ billing, setBilling }) => {
  const yearly = billing === 'yearly';
  return (
    <section className="pricing-hero">
      <div className="hero-mesh"></div>
      <div className="rs-container hero-anim" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <span className="hero-eyebrow"><span className="dot"></span>Simple, transparent pricing</span>
        <h1 className="pricing-h1">Pricing that grows with your team.</h1>
        <p className="pricing-lead">Start free, scale globally. Every plan includes 100+ languages, translation memory, and the full dashboard. No setup fees, cancel any time.</p>
        <div className="billing-toggle" role="tablist" aria-label="Billing period">
          <button role="tab" aria-selected={!yearly} className={`billing-btn ${!yearly ? 'is-active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
          <button role="tab" aria-selected={yearly} className={`billing-btn ${yearly ? 'is-active' : ''}`} onClick={() => setBilling('yearly')}>Yearly <span className="billing-save">2 months free</span></button>
        </div>
      </div>
    </section>
  );
};

const PlanCards = ({ billing, plans }) => {
  const yearly = billing === 'yearly';
  return (
    <section className="section-tight" style={{ paddingTop: 0 }}>
      <div className="rs-container">
        <div className="pricing stagger reveal">
          {plans.map((plan) => {
            const contactSales = plan.monthly == null;
            const isFree = plan.monthly === 0;
            // Yearly total comes from the API (canonical "2 months free");
            // we only compute the monthly-equivalent sub-line locally.
            const price = contactSales ? null : (isFree ? 0 : (yearly ? plan.yearly : plan.monthly));
            const period = contactSales ? '' : (isFree ? 'forever' : (yearly ? '/ year' : '/ month'));
            const monthlyEq = (!contactSales && !isFree && yearly && plan.yearly)
              ? Math.round(plan.yearly / 12) : null;
            return (
              <div key={plan.slug} className={`price-card ${plan.highlight ? 'featured' : ''}`}>
                {plan.highlight && <span className="price-tag">Most Popular</span>}
                <div className="price-name">{plan.name}</div>
                <div className="price-amount">
                  {contactSales ? (
                    <span className="num" style={{ fontSize: '1.6em' }}>Contact us</span>
                  ) : (
                    <>
                      <span className="price-currency">£</span>
                      <span className="num">{price.toLocaleString()}</span>
                      <span className="per">{period}</span>
                    </>
                  )}
                </div>
                {monthlyEq && <div className="price-sub" style={{ fontSize: 12, marginTop: -8 }}>That's £{monthlyEq}/month, billed yearly</div>}
                <div className="price-sub">{plan.description}</div>
                <ul className="price-features">
                  {plan.features.map((f) => (
                    <li key={f} className="price-feature">
                      <svg className="price-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={planTarget(plan)} className={`btn ${plan.highlight ? 'btn-accent' : 'btn-ghost'}`} style={{ marginTop: 'auto' }}>{plan.cta}</Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FoundingNote = () => (
  <section className="section-tight" style={{ paddingTop: 8 }}>
    <div className="rs-container">
      <div className="reveal" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 18, textAlign: 'center', background: 'var(--accent-soft)', border: '1px solid var(--line)', borderRadius: 16, padding: '22px 28px', maxWidth: 880, margin: '0 auto' }}>
        <span style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--ink-2)', maxWidth: 600 }}>
          <strong style={{ color: 'var(--ink)' }}>Pre-launch:</strong> founding customers who join early access lock in these prices for life — and help shape what we build before we launch.
        </span>
        <Link className="btn btn-accent btn-sm" to="/customers">Join early access →</Link>
      </div>
    </div>
  </section>
);

const TrustStrip = () => (
  <section className="section-tight">
    <div className="rs-container">
      <div className="trust-grid reveal stagger">
        <div className="trust-card">
          <div className="trust-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M9 12l2.5 2.5L16 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <h4>14-day money back</h4>
          <p>Try Rush Studio risk-free. If it's not a perfect fit, get a full refund within 14 days.</p>
        </div>
        <div className="trust-card">
          <div className="trust-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </div>
          <h4>SSL encrypted</h4>
          <p>Every payment, every file, every line of memory is protected by 256-bit SSL encryption.</p>
        </div>
        <div className="trust-card">
          <div className="trust-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M2 11h20" stroke="currentColor" strokeWidth="1.8" /></svg>
          </div>
          <h4>Pay any way</h4>
          <div className="pay-methods">
            <span className="pay-chip">VISA</span>
            <span className="pay-chip">Mastercard</span>
            <span className="pay-chip">Amex</span>
            <span className="pay-chip">PayPal</span>
            <span className="pay-chip">SEPA</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Compare = ({ groups, apiPlans }) => {
  // Header price strings come from the API plans (in PLAN_ORDER), so they
  // can't disagree with the plan cards above them.
  const priceCell = (p) =>
    p == null ? '—' :
      p.contactSales ? 'Contact' :
        `£${(p.monthlyGbp || 0).toLocaleString()}`;
  const cols = PLAN_ORDER.map((key) => apiPlans.find((p) => p.key === key));
  return (
  <section className="section" id="compare">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">Compare plans</div>
        <h2>The fine print, in plain English.</h2>
        <p className="section-lead">Every plan includes the full Rush Studio dashboard. These are the differences.</p>
      </div>

      <div className="cmp-table reveal">
        <div className="cmp-row cmp-head">
          <div className="cmp-cell cmp-label-cell"></div>
          {cols.map((p, i) => {
            const isFeatured = p && p.key === 'growth';
            const label = p ? (p.label || p.key) : PLAN_ORDER[i];
            return (
              <div key={PLAN_ORDER[i]} className={`cmp-cell ${isFeatured ? 'cmp-cell-featured' : ''}`}>
                <div className="cmp-plan-name">
                  {label}{isFeatured && <span className="cmp-tag">Popular</span>}
                </div>
                <div className="cmp-plan-price">
                  {priceCell(p)}{p && !p.contactSales && <span>/mo</span>}
                </div>
              </div>
            );
          })}
        </div>

        {groups.map((group) => (
          <Fragment key={group.label}>
            <div className="cmp-row cmp-group-head">
              <div className="cmp-cell cmp-label-cell">{group.label}</div>
              <div className="cmp-cell"></div>
              <div className="cmp-cell"></div>
              <div className="cmp-cell cmp-cell-featured"></div>
              <div className="cmp-cell"></div>
            </div>
            {group.rows.map((row) => (
              <div className="cmp-row" key={row[0]}>
                <div className="cmp-cell cmp-label-cell">{row[0]}</div>
                <div className="cmp-cell"><CompareCell v={row[1]} /></div>
                <div className="cmp-cell"><CompareCell v={row[2]} /></div>
                <div className="cmp-cell cmp-cell-featured"><CompareCell v={row[3]} /></div>
                <div className="cmp-cell"><CompareCell v={row[4]} /></div>
              </div>
            ))}
          </Fragment>
        ))}

        <div className="cmp-row cmp-foot">
          <div className="cmp-cell cmp-label-cell"></div>
          <div className="cmp-cell"><Link className="btn btn-ghost btn-sm" to="/register?plan=free">Start free</Link></div>
          <div className="cmp-cell"><Link className="btn btn-ghost btn-sm" to="/register?plan=essential">Get Started</Link></div>
          <div className="cmp-cell cmp-cell-featured"><Link className="btn btn-accent btn-sm" to="/register?plan=growth">Upgrade Now</Link></div>
          <div className="cmp-cell"><Link className="btn btn-ghost btn-sm" to="/contact">Contact Sales</Link></div>
        </div>
      </div>
    </div>
  </section>
  );
};

const FAQS = [
  { q: 'Is there really a free plan?', a: "Yes. The Free plan covers 1 project and 5,000 words a month with translation memory included — no card required. It's a real plan, not a time-limited trial, so solo translators and first projects can stay on it as long as they like." },
  { q: 'Can I switch plans later?', a: "Yes — upgrade, downgrade, or cancel any time. Changes take effect at the start of your next billing cycle, and we'll pro-rate the difference." },
  { q: 'How are words counted?', a: "Your plan has two monthly allowances: translated words (every word you translate — by hand, from translation memory, or with AI) and AI words (the share of those done by machine/AI translation). Uploading source content is free; words are only counted when they're translated. AI words also count toward your translated-words total." },
  { q: 'When do my allowances reset?', a: 'Both allowances reset at the start of each monthly billing cycle.' },
  { q: 'What if I need more words or projects?', a: 'Upgrade to the next plan at any time — each tier raises both your translated-words and AI-words allowances. Need more than Scale offers? Talk to sales.' },
  { q: 'Do you offer discounts for non-profits or academic use?', a: 'Yes. We offer 30% off any plan for registered non-profits, accredited universities, and open-source projects. Email hello@rushstudio.com with proof.' },
  { q: 'How does the 14-day money-back guarantee work?', a: "If Rush Studio isn't right for you, email us within 14 days of your first payment and we'll refund you in full — no questions asked." },
  { q: 'Is my data secure?', a: 'Rush Studio is SOC 2 Type II certified and GDPR-compliant. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Scale customers can self-host translation memory on-premises.' },
  { q: 'Can I get a custom plan?', a: 'Of course. If you need more than Scale offers — on-prem deployment, custom MT engines, dedicated infrastructure, or volume pricing — talk to our sales team.' },
];

const FAQItem = ({ q, a, open, onToggle }) => (
  <div className={`faq-item ${open ? 'open' : ''}`}>
    <button className="faq-q" onClick={onToggle} aria-expanded={open}>
      <span>{q}</span>
      <span className="faq-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 7l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
    </button>
    <div className="faq-a-wrap">
      <p className="faq-a">{a}</p>
    </div>
  </div>
);

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="section" id="faq">
      <div className="rs-container faq-container">
        <div className="section-head section-head-left reveal" style={{ textAlign: 'left', margin: '0 0 40px' }}>
          <div className="eyebrow">FAQ</div>
          <h2 style={{ fontSize: 36 }}>Questions, answered.</h2>
        </div>
        <div className="faq-list reveal">
          {FAQS.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
        <div className="faq-aside reveal">
          <div className="faq-aside-card">
            <h4>Still have questions?</h4>
            <p>Our team replies in under 2 business hours, every business day.</p>
            <Link className="btn btn-primary btn-sm" to="/contact">Talk to sales</Link>
            <Link className="btn btn-ghost btn-sm" to="/features">Read the docs</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams({ plan: 'free' });
    if (email) qs.set('email', email);
    navigate(`/register?${qs.toString()}`);
  };
  return (
    <section className="section">
      <div className="rs-container">
        <div className="final-cta reveal reveal-scale">
          <h2>Ready to ship languages, not files?</h2>
          <p>Start free, no card required — or join early access to lock in founding-customer pricing.</p>
          <form onSubmit={submit} style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <input
              className="input input-lg"
              placeholder="work@yourcompany.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: 300 }}
            />
            <button className="btn btn-accent btn-lg" type="submit">Start free →</button>
          </form>
        </div>
      </div>
    </section>
  );
};

const RushPricingPage = () => {
  const [billing, setBilling] = useState('monthly');
  const [apiPlans, setApiPlans] = useState([]);
  useRushReveal();

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/plans')
      .then((r) => { if (!cancelled) setApiPlans(r.data?.plans || []); })
      .catch(() => { /* leave empty — children will render their loading state */ });
    return () => { cancelled = true; };
  }, []);

  // Plan cards in declared order; missing plans (e.g. /api/plans failed)
  // just won't render rather than blocking the page.
  const orderedPlans = useMemo(() => {
    return PLAN_ORDER
      .map((key) => apiPlans.find((p) => p.key === key))
      .filter(Boolean)
      .map(buildPlanCard);
  }, [apiPlans]);

  // Compare-table groups: dynamic Usage group prepended to the static rest.
  const orderedApi = useMemo(
    () => PLAN_ORDER.map((key) => apiPlans.find((p) => p.key === key)).filter(Boolean),
    [apiPlans],
  );
  const compareGroups = useMemo(() => {
    if (orderedApi.length === 0) return STATIC_COMPARE_GROUPS;
    return [{ label: 'Usage', rows: buildUsageRows(orderedApi) }, ...STATIC_COMPARE_GROUPS];
  }, [orderedApi]);

  return (
    <div className="rs">
      <RushNav active="pricing" />
      <PricingHero billing={billing} setBilling={setBilling} />
      <PlanCards billing={billing} plans={orderedPlans} />
      <FoundingNote />
      <TrustStrip />
      <Compare groups={compareGroups} apiPlans={apiPlans} />
      <FAQ />
      <FinalCTA />
      <RushFooter />
    </div>
  );
};

export default RushPricingPage;
